import React from "react";
import { graphql, HeadProps } from "gatsby";
import { Layout } from "@/components/ui/Layout/Layout";
import { Post } from "@/components/business/BlogPosts/Post/Post";
import { PostContent } from "@/components/business/BlogPosts/PostContent/PostContent";
import { PostAuthor } from "@/components/business/BlogPosts/PostAuthor/PostAuthor";
import { IArticleContentful } from "@/interfaces/IArticleContentful.interface";
import { Location } from "@/interfaces/Location";
import { useDimensions } from "@/hooks/useDimensions";
import { QuotationPostCard } from "@/components/business/QuotationPostCard";
import { URL_LANDINGPAGE } from "@/constants/env";
import { getReadingTime } from "@/utils/functions";

const BlogPostTemplate = ({
  data: { blog },
  location,
}: {
  data: { blog: IArticleContentful };
  location: Location;
}) => {
  const { isMobile } = useDimensions();
  return (
    <>
      <Layout location={location} showBreadcrumbs>
        <Post
          title={blog.title}
          subtitle={blog.subtitle}
          author={blog.author.name}
          publishDate={blog.publishDate}
          image={blog.heroImage.url}
          linkedinUrl={blog.author.linkedin}
          readingTime={`${getReadingTime(JSON.parse(blog.body.raw))} mins`}
        />
        <PostContent blog={blog} />
        <PostAuthor blog={blog} />
        {isMobile && <QuotationPostCard mobile />}
      </Layout>
    </>
  );
};
export function Head({
  data: { blog },
}: HeadProps<{ blog: IArticleContentful }>) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    image: blog.heroImage.url,
    author: {
      "@type": "Person",
      name: blog.author.name,
    },
    datePublished: blog.publishDate,
    description: blog.description ?? blog.title,
    articleBody: blog.body.raw, // Usa el cuerpo del artículo
    url: `${URL_LANDINGPAGE}/blog/${blog.urlSlug}`,
  };
  return (
    <>
      <title>{blog.title}</title>
      <meta name="description" content={blog?.description || blog.title} />
      <meta property="og:title" content={blog.title} />
      <meta
        property="og:description"
        content={blog?.description || blog.title}
      />
      <meta property="og:image" content={blog.heroImage.url} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`${URL_LANDINGPAGE}/blog/${blog.urlSlug}`} />
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </>
  );
}

export default BlogPostTemplate;

export const query = graphql`
  query ($slug: String!) {
    blog: contentfulBlogPost(urlSlug: { eq: $slug }) {
      id
      author {
        email
        linkedin
        image {
          url
        }
        description {
          description
        }
        name
      }
      blogOfTheWeek
      body {
        raw
        references {
          ... on ContentfulAsset {
            contentful_id
            title
            gatsbyImageData
            __typename
          }
        }
      }
      category
      heroImage {
        url
      }
      publishDate
      title
      subtitle
      description
      authorIllustration {
        name
      }
    }
  }
`;
